import { c as _c } from "react/compiler-runtime";
import { throttle } from 'es-toolkit';
import { useEffect, useRef, useState } from 'react';
import { STORAGE_KEY } from '@/constants';
import { useHistoryStore } from '@/stores/HistoryStore/useHistoryStore';
export interface TimelineScrollData {
  expandedIds: string[];
  scrollPositions: Record<string, number>;
}
interface Section {
  id: string;
  count: number;
}
interface UseTimelineScrollHistoryParams {
  sections: Section[];
  filter: 'available' | 'new' | 'deadline' | 'scrap' | null;
  fullPath: string | null;
}
const getStorageData = (key: string): TimelineScrollData | undefined => {
  try {
    const data = sessionStorage.getItem(STORAGE_KEY.TIMELINE_SCROLL_HISTORY);
    if (!data) return undefined;
    const parsed = JSON.parse(data);
    return parsed[key];
  } catch {
    return undefined;
  }
};
const setStorageData = (key: string, data: TimelineScrollData) => {
  try {
    const current = sessionStorage.getItem(STORAGE_KEY.TIMELINE_SCROLL_HISTORY);
    const parsed = current ? JSON.parse(current) : {};
    parsed[key] = data;
    sessionStorage.setItem(STORAGE_KEY.TIMELINE_SCROLL_HISTORY, JSON.stringify(parsed));
  } catch {
    // ignore storage errors
  }
};
export const useTimelineScrollHistory = t0 => {
  const $ = _c(18);
  const {
    sections,
    filter,
    fullPath
  } = t0;
  const history = useHistoryStore(_temp);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const sectionRefs = useRef(t1);
  let t2;
  if ($[1] !== fullPath || $[2] !== history) {
    t2 = () => {
      if (!fullPath) {
        return;
      }
      const historyArray = Array.from(history.values());
      const recentPaths = historyArray.slice(-2);
      const currentPathname = fullPath.split("?")[0];
      const hasCurrentPath = recentPaths.some(path => path.split("?")[0] === currentPathname);
      if (hasCurrentPath && recentPaths.length >= 2) {
        return getStorageData(fullPath);
      }
    };
    $[1] = fullPath;
    $[2] = history;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [restoredData] = useState(t2);
  let t3;
  if ($[4] !== restoredData?.scrollPositions) {
    t3 = restoredData?.scrollPositions ?? {};
    $[4] = restoredData?.scrollPositions;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const scrollPositions = useRef(t3);
  let t4;
  if ($[6] !== restoredData?.expandedIds) {
    t4 = () => new Set(restoredData?.expandedIds ?? ["current"]);
    $[6] = restoredData?.expandedIds;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const [expandedIds, setExpandedIds] = useState(t4);
  const [previousFilter, setPreviousFilter] = useState(filter);
  if (previousFilter !== filter) {
    setPreviousFilter(filter);
    if (filter && filter !== "available") {
      const sectionsWithContent = sections.filter(_temp2).map(_temp3);
      setExpandedIds(new Set(sectionsWithContent));
    } else {
      setExpandedIds(new Set(["current"]));
    }
  }
  let t5;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      if (Object.keys(scrollPositions.current).length === 0) {
        return;
      }
      requestAnimationFrame(() => {
        Object.entries(scrollPositions.current).forEach(t7 => {
          const [id, pos] = t7;
          const el = sectionRefs.current[id];
          if (el && pos > 0) {
            el.scrollTo({
              top: pos
            });
          }
        });
      });
    };
    t6 = [];
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = () => {
      const handlers = new Map();
      Object.entries(sectionRefs.current).forEach(t9 => {
        const [id_0, el_0] = t9;
        if (!el_0) {
          return;
        }
        const handler = throttle(() => {
          const pos_0 = el_0.scrollTop;
          const lastPos = scrollPositions.current[id_0] ?? 0;
          if (pos_0 === 0 && lastPos > 100) {
            return;
          }
          scrollPositions.current[id_0] = pos_0;
        }, 300);
        el_0.addEventListener("scroll", handler, {
          passive: true
        });
        handlers.set(el_0, handler);
      });
      return () => {
        handlers.forEach(_temp4);
      };
    };
    t8 = [];
    $[10] = t7;
    $[11] = t8;
  } else {
    t7 = $[10];
    t8 = $[11];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[12] !== expandedIds || $[13] !== fullPath) {
    t9 = () => {
      const currentScrollPositions = scrollPositions.current;
      return () => {
        if (fullPath) {
          setStorageData(fullPath, {
            expandedIds: Array.from(expandedIds),
            scrollPositions: currentScrollPositions
          });
        }
      };
    };
    t10 = [fullPath, expandedIds];
    $[12] = expandedIds;
    $[13] = fullPath;
    $[14] = t10;
    $[15] = t9;
  } else {
    t10 = $[14];
    t9 = $[15];
  }
  useEffect(t9, t10);
  let t11;
  if ($[16] !== expandedIds) {
    t11 = {
      sectionRefs,
      expandedIds,
      setExpandedIds
    };
    $[16] = expandedIds;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  return t11;
};
function _temp(state) {
  return state.customHistory;
}
function _temp2(section) {
  return section.count > 0;
}
function _temp3(section_0) {
  return section_0.id;
}
function _temp4(handler_0, el_1) {
  if (handler_0.cancel) {
    handler_0.cancel();
  }
  el_1.removeEventListener("scroll", handler_0);
}